import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Ban, History, Loader2, Pencil, Plus, TrendingUp } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { CompanyTabs } from "@/components/shared/CompanyTabs";
import { BORDER, FormDrawer } from "@/components/shared/FormDrawer";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { formatDate } from "@/lib/utils";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { formatCurrency } from "@/modules/expenses/components/CurrencyAmount";
import {
  useIncomeAction,
  useIncomeList,
  useIncomeOptions,
  type FinanceIncome,
  type IncomeStatus,
} from "../hooks/useFinanceIncome";

const CURRENCIES = ["AED", "INR", "USD", "EUR", "GBP"];
const ICON_BUTTON =
  "inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground";
const today = () => new Date().toISOString().slice(0, 10);

function NewIncomeDrawer({ company, onClose }: { company: { id: string; name: string }; onClose: () => void }) {
  const action = useIncomeAction();
  const { data: options } = useIncomeOptions();
  const [receivedDate, setReceivedDate] = useState(today());
  const [accountId, setAccountId] = useState("");
  const [clientCompanyId, setClientCompanyId] = useState("");
  const [currency, setCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [creditedAmount, setCreditedAmount] = useState("");
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const accounts = options?.accounts.filter((a) => a.company.id === company.id) ?? [];
  const account = accounts.find((a) => a.id === accountId);
  const paidCurrency = currency || account?.currency || "";
  const converted = !!account && paidCurrency !== account.currency;
  const rate =
    converted && Number(amount) > 0 && Number(creditedAmount) > 0
      ? Number(creditedAmount) / Number(amount)
      : null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await action.mutateAsync({
        method: "POST",
        path: "",
        body: {
          receivedDate,
          accountId,
          clientCompanyId: clientCompanyId || null,
          amount: Number(amount),
          currency: paidCurrency,
          creditedAmount: converted ? Number(creditedAmount) : null,
          reference: reference || null,
          description: description || null,
        },
      });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <FormDrawer title={`New Income: ${company.name}`} onClose={onClose} onSubmit={handleSubmit} error={error} busy={action.isPending}>
      <div className="space-y-2">
        <Label htmlFor="inc-date">
          Date received <span className="text-destructive">*</span>
        </Label>
        <Input id="inc-date" type="date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} required />
      </div>
      <div className="space-y-2">
        <Label htmlFor="inc-account">
          Received into <span className="text-destructive">*</span>
        </Label>
        <Select
          id="inc-account"
          value={accountId}
          onChange={(e) => {
            setAccountId(e.target.value);
            setCurrency("");
          }}
          required
        >
          <option value="">Select an account...</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.currency})
            </option>
          ))}
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="inc-client">Client</Label>
        <Select id="inc-client" value={clientCompanyId} onChange={(e) => setClientCompanyId(e.target.value)}>
          <option value="">No client</option>
          {options?.clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <p className="text-xs text-muted-foreground">
          Clients are external companies. Add a missing one under Business Structure → Companies.
        </p>
      </div>

      {account && (
        <>
          <div className="grid grid-cols-[110px_1fr] gap-3">
            <div className="space-y-2">
              <Label htmlFor="inc-currency">Paid in</Label>
              <Select id="inc-currency" value={paidCurrency} onChange={(e) => setCurrency(e.target.value)}>
                {[...new Set([account.currency, ...CURRENCIES])].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="inc-amount">
                Amount paid ({paidCurrency}) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="inc-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>
          </div>
          {converted && (
            <div className="space-y-2">
              <Label htmlFor="inc-credited">
                Credited to account ({account.currency}) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="inc-credited"
                type="number"
                min="0.01"
                step="0.01"
                value={creditedAmount}
                onChange={(e) => setCreditedAmount(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Enter exactly what the bank statement shows, so the account tallies.
                {rate ? ` Rate achieved: 1 ${paidCurrency} = ${rate.toFixed(4)} ${account.currency}.` : ""}
              </p>
            </div>
          )}
        </>
      )}

      <div className="space-y-2">
        <Label htmlFor="inc-ref">Reference</Label>
        <Input
          id="inc-ref"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="e.g. bank reference or invoice number"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="inc-desc">Description</Label>
        <Input
          id="inc-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. TillShield POC, part payment"
        />
      </div>
    </FormDrawer>
  );
}

function EditIncomeDrawer({ income, onClose }: { income: FinanceIncome; onClose: () => void }) {
  const action = useIncomeAction();
  const { data: options } = useIncomeOptions();
  const [clientCompanyId, setClientCompanyId] = useState(income.clientCompanyId ?? "");
  const [reference, setReference] = useState(income.reference ?? "");
  const [description, setDescription] = useState(income.description ?? "");
  const [notes, setNotes] = useState(income.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await action.mutateAsync({
        method: "PUT",
        path: `/${income.id}`,
        body: { clientCompanyId: clientCompanyId || null, reference, description, notes },
      });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <FormDrawer title={`Edit Income: ${income.code}`} onClose={onClose} onSubmit={handleSubmit} error={error} busy={action.isPending}>
      <p className="text-xs text-muted-foreground">
        The date, account and amounts cannot be changed. If they are wrong, cancel this income and
        enter it again.
      </p>
      <div className="space-y-2">
        <Label htmlFor="ed-client">Client</Label>
        <Select id="ed-client" value={clientCompanyId} onChange={(e) => setClientCompanyId(e.target.value)}>
          <option value="">No client</option>
          {options?.clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="ed-ref">Reference</Label>
        <Input id="ed-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ed-desc">Description</Label>
        <Input id="ed-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ed-notes">Notes</Label>
        <Input id="ed-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
    </FormDrawer>
  );
}

function CancelIncomeDrawer({ income, onClose }: { income: FinanceIncome; onClose: () => void }) {
  const action = useIncomeAction();
  const [entryDate, setEntryDate] = useState(today());
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await action.mutateAsync({ method: "POST", path: `/${income.id}/cancel`, body: { entryDate, reason } });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <FormDrawer title={`Cancel Income: ${income.code}`} onClose={onClose} onSubmit={handleSubmit} error={error} busy={action.isPending}>
      <p className="text-xs text-muted-foreground">
        Takes {formatCurrency(Number(income.creditedAmount), income.creditedCurrency)} back out of{" "}
        {income.account.name} with a reversing entry. The income stays on record as cancelled.
      </p>
      <div className="space-y-2">
        <Label htmlFor="cx-date">
          Reversal date <span className="text-destructive">*</span>
        </Label>
        <Input id="cx-date" type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} required />
      </div>
      <div className="space-y-2">
        <Label htmlFor="cx-reason">
          Reason <span className="text-destructive">*</span>
        </Label>
        <Input
          id="cx-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Entered against the wrong account"
          required
        />
      </div>
    </FormDrawer>
  );
}

export default function IncomePage() {
  const { user } = useAuth();
  const permissions = user?.permissions?.finance_income;
  const [companyId, setCompanyId] = useState("");
  const [status, setStatus] = useState<IncomeStatus | "">("");
  // Loaded for every company, so each tab can show its count.
  const { data: allIncomes = [], isLoading, isError, error } = useIncomeList({ status });
  const { data: options } = useIncomeOptions();
  const [drawer, setDrawer] = useState<
    { kind: "new" } | { kind: "edit"; income: FinanceIncome } | { kind: "cancel"; income: FinanceIncome } | null
  >(null);
  const [activity, setActivity] = useState<FinanceIncome | null>(null);

  const companies = useMemo(() => {
    const seen = new Map<string, string>();
    for (const a of options?.accounts ?? []) seen.set(a.company.id, a.company.name);
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [options]);

  useEffect(() => {
    if (!companyId && companies.length) setCompanyId(companies[0].id);
  }, [companyId, companies]);

  const company = companies.find((c) => c.id === companyId);
  const incomes = useMemo(() => allIncomes.filter((i) => i.companyId === companyId), [allIncomes, companyId]);
  const tabs = companies.map((c) => ({ ...c, count: allIncomes.filter((i) => i.companyId === c.id).length }));

  // Received totals per currency credited, so PX (AED) and TX (INR) never mix.
  const totals = useMemo(() => {
    const byCurrency = new Map<string, number>();
    for (const i of incomes) {
      if (i.status !== "RECEIVED") continue;
      const cents = Math.round(Number(i.creditedAmount) * 100);
      byCurrency.set(i.creditedCurrency, (byCurrency.get(i.creditedCurrency) ?? 0) + cents);
    }
    return [...byCurrency.entries()].map(([c, cents]) => formatCurrency(cents / 100, c));
  }, [incomes]);

  return (
    <AppLayout breadcrumb={[{ label: "PhazeX Hub", href: "/" }, { label: "Finance" }, { label: "Income" }]}>
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TrendingUp className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">Income</h1>
          </div>
          <div className="flex items-center gap-2">
            {permissions?.canExport && (
              <ExportButton module="finance-income" filters={{ companyId: companyId || undefined, status: status || undefined }} />
            )}
            {permissions?.canCreate && company && (
              <Button onClick={() => setDrawer({ kind: "new" })}>
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                New Income
              </Button>
            )}
          </div>
        </div>

        {tabs.length > 0 && <CompanyTabs companies={tabs} value={companyId} onChange={setCompanyId} />}

        <div className="flex flex-wrap items-center gap-3">
          <Select value={status} onChange={(e) => setStatus(e.target.value as IncomeStatus | "")} className="w-40">
            <option value="">All statuses</option>
            <option value="RECEIVED">Received</option>
            <option value="CANCELLED">Cancelled</option>
          </Select>
          {totals.length > 0 && (
            <span className="text-sm text-muted-foreground">Received: {totals.join(" · ")}</span>
          )}
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            {(error as Error)?.message ?? "Failed to load income"}
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border bg-white" style={BORDER}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground" style={BORDER}>
                  <th className="px-4 py-3 font-medium">Received</th>
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Client</th>
                  <th className="px-4 py-3 font-medium">Into</th>
                  <th className="px-4 py-3 text-right font-medium">Paid</th>
                  <th className="px-4 py-3 text-right font-medium">Credited</th>
                  <th className="px-4 py-3 font-medium">Reference</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} />
                    </td>
                  </tr>
                )}
                {!isLoading && incomes.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                      No income recorded for {company?.name ?? "this company"} yet
                    </td>
                  </tr>
                )}
                {incomes.map((i) => {
                  const cancelled = i.status === "CANCELLED";
                  return (
                    <tr key={i.id} className="border-b last:border-0 align-top" style={BORDER}>
                      <td className="whitespace-nowrap px-4 py-3">{formatDate(i.receivedDate)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{i.code}</td>
                      <td className="px-4 py-3">
                        <div className={cancelled ? "text-muted-foreground line-through" : "font-medium"}>
                          {i.clientCompany?.name ?? "—"}
                        </div>
                        {i.description && <div className="text-xs text-muted-foreground">{i.description}</div>}
                        {cancelled && (
                          <div className="text-xs text-destructive">Cancelled: {i.cancelReason}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <Link to={`/finance/accounts/${i.accountId}`} className="hover:underline">
                          {i.company.name} · {i.account.name}
                        </Link>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                        {formatCurrency(Number(i.amount), i.currency)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                        {formatCurrency(Number(i.creditedAmount), i.creditedCurrency)}
                        {i.rate && (
                          <div className="text-xs text-muted-foreground">@ {Number(i.rate).toFixed(4)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{i.reference ?? "—"}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button type="button" onClick={() => setActivity(i)} className={ICON_BUTTON} title="Activity history">
                          <History className="h-[14px] w-[14px]" strokeWidth={1.5} />
                        </button>
                        {permissions?.canEdit && (
                          <button type="button" onClick={() => setDrawer({ kind: "edit", income: i })} className={ICON_BUTTON} title="Edit details">
                            <Pencil className="h-[14px] w-[14px]" strokeWidth={1.5} />
                          </button>
                        )}
                        {permissions?.canDelete && !cancelled && (
                          <button
                            type="button"
                            onClick={() => setDrawer({ kind: "cancel", income: i })}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
                            title="Cancel income"
                          >
                            <Ban className="h-[14px] w-[14px]" strokeWidth={1.5} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {drawer?.kind === "new" && company && <NewIncomeDrawer company={company} onClose={() => setDrawer(null)} />}
      {drawer?.kind === "edit" && <EditIncomeDrawer income={drawer.income} onClose={() => setDrawer(null)} />}
      {drawer?.kind === "cancel" && <CancelIncomeDrawer income={drawer.income} onClose={() => setDrawer(null)} />}
      {activity && (
        <ActivityHistoryDrawer
          isOpen
          onClose={() => setActivity(null)}
          entityType="finance_income"
          entityId={activity.id}
          entityLabel={activity.code}
        />
      )}
    </AppLayout>
  );
}
